// ============================================================================
// GroupsPage - find, join, and create player groups
// ============================================================================
// Groups are the rally point for regular games. Every group is a picture
// card: your groups first (with unread and request badges), then a swipe
// carousel of groups to discover, one at a time. Private groups join by
// invite code or by request; chat, games and members live on the group
// page.
// ============================================================================
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Heading, Text, VStack, HStack, Spinner, Button, Input,
  Textarea, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody,
  ModalCloseButton, Switch, useToast, SimpleGrid,
} from '@chakra-ui/react';
import { Search, Plus, KeyRound } from 'lucide-react';
import { gameApiService } from '../services/gameApiService';
import GroupCard, { GroupCardData } from '../components/groups/GroupCard';
import GroupCarousel from '../components/groups/GroupCarousel';
import GroupCover, { GROUP_COVER_THEMES } from '../components/groups/GroupCover';

const CARD_BG = '#0f1722';

const GroupsPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [myGroups, setMyGroups] = useState<GroupCardData[]>([]);
  const [suggested, setSuggested] = useState<GroupCardData[]>([]);
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<GroupCardData[] | null>(null);
  const [joinCode, setJoinCode] = useState('');
  const [joiningCode, setJoiningCode] = useState(false);
  const [joiningId, setJoiningId] = useState<string | null>(null);

  // Create modal
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newPublic, setNewPublic] = useState(true);
  const [newTheme, setNewTheme] = useState(0);
  const [creating, setCreating] = useState(false);

  const loadAll = async () => {
    const [mine, sugg] = await Promise.all([gameApiService.getMyGroups(), gameApiService.getSuggestedGroups()]);
    if (mine.success) setMyGroups((mine.groups || []) as GroupCardData[]);
    if (sugg.success) setSuggested((sugg.groups || []) as GroupCardData[]);
    setLoading(false);
  };
  useEffect(() => { loadAll(); }, []);

  const handleSearch = async () => {
    if (query.trim().length < 2) { setResults(null); return; }
    setSearching(true);
    const r = await gameApiService.searchGroups(query.trim());
    setResults(r.success ? ((r.groups || []) as GroupCardData[]) : []);
    setSearching(false);
  };

  const handleJoin = async (groupId: string) => {
    setJoiningId(groupId);
    const r = await gameApiService.joinGroup({ groupId });
    setJoiningId(null);
    if (r.success) {
      if (r.requested) toast({ title: 'Request sent', description: 'The group owner will accept or decline it.', status: 'info', duration: 4000 });
      navigate(`/groups/${groupId}`);
    } else toast({ title: r.error || 'Could not join', status: 'error', duration: 4000 });
  };

  const handleJoinByCode = async () => {
    const code = joinCode.trim().toUpperCase();
    if (!code) return;
    setJoiningCode(true);
    const r = await gameApiService.joinGroup({ code });
    setJoiningCode(false);
    if (r.success && r.groupId) navigate(`/groups/${r.groupId}`);
    else toast({ title: r.error || 'No group with that code', status: 'error', duration: 4000 });
  };

  const handleCreate = async () => {
    if (newName.trim().length < 3) {
      toast({ title: 'Group name needs at least 3 characters', status: 'warning', duration: 3000 });
      return;
    }
    setCreating(true);
    const r = await gameApiService.createGroup(newName.trim(), newDesc.trim(), newPublic);
    if (r.success && r.groupId) {
      await gameApiService.updateGroup(r.groupId, { coverTheme: newTheme }).catch(() => {});
      setCreating(false);
      setShowCreate(false);
      navigate(`/groups/${r.groupId}`);
    } else {
      setCreating(false);
      toast({ title: r.error || 'Could not create the group', status: 'error', duration: 4000 });
    }
  };

  const discover = results !== null ? results : suggested;

  return (
    <Box minH="100vh" bg="#0a1420" pb={16}>
      <Container maxW="container.md" pt={6}>
        <HStack justify="space-between" mb={1}>
          <Heading size="lg" color="white">Groups</Heading>
          <HStack>
            <Button size="sm" colorScheme="purple" leftIcon={<Plus size={15} />} onClick={() => setShowCreate(true)}>
              Create
            </Button>
            <Button size="sm" variant="outline" colorScheme="cyan" onClick={() => navigate(-1)}>Back</Button>
          </HStack>
        </HStack>
        <Text fontSize="sm" color="gray.500" mb={5}>
          Your poker crew in one place. Chat, rally the group to a table, or set a game for later.
        </Text>

        {/* My groups */}
        <Heading size="sm" color="gray.300" mb={3}>My groups</Heading>
        {loading ? (
          <Box textAlign="center" py={8}><Spinner color="cyan.400" /></Box>
        ) : myGroups.length === 0 ? (
          <Box bg={CARD_BG} border="1px solid rgba(148,163,184,0.2)" borderRadius="xl" p={6} textAlign="center" mb={6}>
            <Text color="gray.400" fontSize="sm">
              You are not in any groups yet. Swipe through the groups below, or create one for your crew.
            </Text>
          </Box>
        ) : (
          <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3} mb={7}>
            {myGroups.map(g => <GroupCard key={g.id} group={g} onOpen={(id) => navigate(`/groups/${id}`)} />)}
          </SimpleGrid>
        )}

        {/* Discover */}
        <HStack justify="space-between" align="baseline" mb={2}>
          <Heading size="sm" color="gray.300">Find a group</Heading>
          <Text fontSize="xs" color="gray.500">Swipe to browse</Text>
        </HStack>
        <HStack mb={4}>
          <Input
            placeholder="Search groups by name"
            value={query} onChange={e => setQuery(e.target.value)}
            bg={CARD_BG} borderColor="whiteAlpha.200" color="white" size="sm"
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
          />
          <Button size="sm" colorScheme="cyan" leftIcon={<Search size={14} />} isLoading={searching} onClick={handleSearch}>
            Search
          </Button>
          {results !== null && (
            <Button size="sm" variant="ghost" color="gray.400" onClick={() => { setResults(null); setQuery(''); }}>Clear</Button>
          )}
        </HStack>
        {loading ? null : discover.length === 0 ? (
          <Text color="gray.500" fontSize="sm" mb={6}>
            {results !== null ? 'No public groups match that name.' : 'Nothing new to discover right now. Create a group and invite your friends.'}
          </Text>
        ) : (
          <Box mb={7}>
            <GroupCarousel groups={discover} onOpen={(id) => navigate(`/groups/${id}`)} onJoin={handleJoin} joiningId={joiningId} />
          </Box>
        )}

        {/* Join by code */}
        <Box bg={CARD_BG} border="1px solid rgba(148,163,184,0.2)" borderRadius="xl" p={4}>
          <HStack mb={2}>
            <Box color="gray.400"><KeyRound size={16} /></Box>
            <Text color="white" fontWeight="700" fontSize="sm">Have an invite code?</Text>
          </HStack>
          <HStack>
            <Input
              placeholder="6-character code"
              value={joinCode} onChange={e => setJoinCode(e.target.value.toUpperCase())} maxLength={6}
              bg="gray.800" borderColor="whiteAlpha.200" color="white" size="sm" maxW="220px"
              onKeyDown={e => e.key === 'Enter' && handleJoinByCode()}
            />
            <Button size="sm" colorScheme="cyan" variant="outline" isLoading={joiningCode} onClick={handleJoinByCode}>
              Join
            </Button>
          </HStack>
        </Box>
      </Container>

      {/* Create modal */}
      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} isCentered scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent bg="#1C1C1E" color="white" mx={4}>
          <ModalHeader fontSize="md">Create a group</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack align="stretch" spacing={4}>
              <GroupCover coverTheme={newTheme} name={newName || 'New group'} height={110}>
                <Box position="absolute" left="12px" bottom="10px">
                  <Text color="white" fontWeight="800" fontSize="lg" textShadow="0 1px 6px rgba(0,0,0,0.7)">{newName || 'Your group name'}</Text>
                </Box>
              </GroupCover>
              <Box>
                <Text fontSize="sm" mb={1} color="gray.400">Cover</Text>
                <HStack spacing={2} flexWrap="wrap">
                  {GROUP_COVER_THEMES.map((t, i) => (
                    <Box key={t.name} w="34px" h="34px" borderRadius="8px" cursor="pointer" title={t.name}
                      bg={`linear-gradient(135deg, ${t.from}, ${t.to})`}
                      border={i === newTheme ? '2px solid #a78bfa' : '2px solid transparent'}
                      onClick={() => setNewTheme(i)} />
                  ))}
                </HStack>
                <Text fontSize="xs" color="gray.500" mt={1}>You can upload a photo from the group page after creating it.</Text>
              </Box>
              <Box>
                <Text fontSize="sm" mb={1} color="gray.400">Name</Text>
                <Input value={newName} onChange={e => setNewName(e.target.value)} maxLength={40}
                  placeholder="Friday Night Crew" bg="gray.800" borderColor="gray.600" />
              </Box>
              <Box>
                <Text fontSize="sm" mb={1} color="gray.400">Description (optional)</Text>
                <Textarea value={newDesc} onChange={e => setNewDesc(e.target.value)} maxLength={200}
                  placeholder="What this group plays and when" bg="gray.800" borderColor="gray.600" rows={2} />
              </Box>
              <HStack justify="space-between">
                <Box>
                  <Text fontSize="sm">Public group</Text>
                  <Text fontSize="xs" color="gray.500">
                    {newPublic ? 'Anyone can find and join it' : 'Players request to join, or use your invite code'}
                  </Text>
                </Box>
                <Switch colorScheme="purple" isChecked={newPublic} onChange={e => setNewPublic(e.target.checked)} />
              </HStack>
              <Button colorScheme="purple" isLoading={creating} onClick={handleCreate}>
                Create group
              </Button>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default GroupsPage;
